import { useBrands } from "../../hooks/useBrand";
import { type FormEvent, useMemo, useRef, useState } from "react";
import { ApiError } from "../../lib/api";
import {
  CLI_AUTH_TOKEN_SCOPES,
  type CliAuthTokenMetadata,
  type CliAuthTokenScope,
  useCliAuthTokens,
  useCreateCliAuthToken,
  useRevokeCliAuthToken,
} from "../../hooks/useCliAuthTokens";
import { Button } from "../ui/Button";
import { Card } from "../ui/Card";
import { CopyButton } from "../ui/CopyButton";
import { Input } from "../ui/Input";
import { Modal } from "../ui/Modal";

const SCOPE_COPY: Record<CliAuthTokenScope, { title: string; description: string }> = {
  "capabilities:read": {
    title: "Read available capabilities",
    description: "Lets the consumer inspect which HelloConvo capabilities it can use.",
  },
  "tasks:read": {
    title: "Read Actions and Runs",
    description: "Lets the consumer inspect Actions and existing Runs.",
  },
  "tasks:run": {
    title: "Run Actions",
    description: "Lets the consumer execute HelloConvo Actions.",
  },
  "documents:read": {
    title: "Read brand docs",
    description: "Lets the consumer inspect brand context, corpus, and saved documents.",
  },
  "documents:write": {
    title: "Write brand docs",
    description: "Lets the consumer save research and artifacts back into HelloConvo.",
  },
  "publishing:read": { title: "Read publishing", description: "Read destination permissions, prepared variants, and delivery receipts." },
  "publishing:write": { title: "Prepare publishing", description: "Prepare and validate destination variants. Human approval is still required." },
  "publishing:submit": { title: "Submit approved publishing", description: "Submit an unchanged, explicitly approved revision and reconcile its delivery." },
  "publishing:handoff": { title: "Publish to your site (site agent)", description: "List, export and claim blog posts you marked Ready to publish, and report the live URL. Cannot approve or mark posts ready." },
};

function formatTimestamp(value: string | null): string {
  if (!value) return "Never";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleString();
}

function parseExpiryInput(value: string): string | null {
  const trimmed = value.trim();
  if (!trimmed) return null;
  const parsed = new Date(trimmed);
  if (Number.isNaN(parsed.getTime())) return null;
  if (parsed.getTime() <= Date.now()) return null;
  return parsed.toISOString();
}

export function ApiTokenManager({
  brandName,
}: {
  brandName?: string | null;
}) {
  const [authority, setAuthority] = useState<"project" | "account">("project");
  const [selectedBrands, setSelectedBrands] = useState<string[]>([]);
  const projectInputRef = useRef<HTMLInputElement>(null);
  const environmentInputRef = useRef<HTMLInputElement>(null);
  const agentInputRef = useRef<HTMLInputElement>(null);
  const purposeInputRef = useRef<HTMLInputElement>(null);
  const brandsQuery = useBrands();
  const [selectedScopes, setSelectedScopes] = useState<CliAuthTokenScope[]>([
    "capabilities:read",
    "tasks:read",
    "tasks:run",
  ]);
  const [formError, setFormError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [revokingTokenId, setRevokingTokenId] = useState<string | null>(null);
  const [revealedToken, setRevealedToken] = useState<{
    token: string;
    metadata: CliAuthTokenMetadata;
  } | null>(null);
  const nameInputRef = useRef<HTMLInputElement>(null);
  const expiryInputRef = useRef<HTMLInputElement>(null);

  const tokensQuery = useCliAuthTokens();
  const createToken = useCreateCliAuthToken();
  const revokeToken = useRevokeCliAuthToken();

  const sortedTokens = useMemo(
    () => [...(tokensQuery.data?.data.tokens ?? [])].sort((a, b) => {
      const aRevoked = a.revokedAt ? 1 : 0;
      const bRevoked = b.revokedAt ? 1 : 0;
      if (aRevoked !== bRevoked) return aRevoked - bRevoked;
      return b.createdAt.localeCompare(a.createdAt);
    }),
    [tokensQuery.data?.data.tokens],
  );

  function toggleScope(scope: CliAuthTokenScope) {
    setSelectedScopes((current) =>
      current.includes(scope)
        ? current.filter((candidate) => candidate !== scope)
        : [...current, scope],
    );
  }

  async function handleCreateToken(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setFormError(null);
    setActionError(null);
    const rawName = nameInputRef.current?.value ?? "";
    const rawExpiry = expiryInputRef.current?.value ?? "";

    if (!rawName.trim()) {
      setFormError("Token name is required.");
      return;
    }

    if (selectedScopes.length === 0) {
      setFormError("Select at least one scope.");
      return;
    }

    const expiresAt = parseExpiryInput(rawExpiry);
    if (rawExpiry.trim() && !expiresAt) {
      setFormError("Expiry must be a valid future date and time.");
      return;
    }

    const projectGrant = authority === "project" ? {
      projectId: projectInputRef.current?.value.trim() ?? "",
      environment: environmentInputRef.current?.value.trim() ?? "",
      agentId: agentInputRef.current?.value.trim() ?? "",
      purpose: purposeInputRef.current?.value.trim() ?? "",
      brandIds: selectedBrands,
    } : undefined;
    if (projectGrant && (!expiresAt || !projectGrant.brandIds.length
      || [projectGrant.projectId, projectGrant.environment, projectGrant.agentId, projectGrant.purpose]
        .some(value => !/^[a-zA-Z0-9][a-zA-Z0-9._-]{0,119}$/.test(value)))) {
      setFormError("Project access requires a project, environment, agent, purpose, selected brands, and future expiry.");
      return;
    }
    try {
      const result = await createToken.mutateAsync({
        name: rawName.trim(),
        scopes: selectedScopes,
        expiresAt,
        projectGrant,
      });
      setRevealedToken({
        token: result.data.token,
        metadata: result.data.metadata,
      });
      if (nameInputRef.current) nameInputRef.current.value = "";
      if (expiryInputRef.current) expiryInputRef.current.value = "";
      setSelectedScopes(["capabilities:read", "tasks:read", "tasks:run"]);
    } catch (error) {
      if (error instanceof ApiError) {
        setFormError(error.message);
        return;
      }
      setFormError("Failed to create token.");
    }
  }

  async function handleRevokeToken(tokenId: string) {
    const confirmed = window.confirm("Revoke this API token? Existing consumers will stop working.");
    if (!confirmed) return;
    setActionError(null);
    setRevokingTokenId(tokenId);
    try {
      await revokeToken.mutateAsync(tokenId);
    } catch (error) {
      if (error instanceof ApiError) {
        setActionError(error.message);
      } else {
        setActionError("Failed to revoke token.");
      }
    } finally {
      setRevokingTokenId(null);
    }
  }

  return (
    <div className="space-y-6">
      <Card className="rounded-[20px] border border-slate-200 bg-white shadow-sm">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="max-w-2xl">
            <h2 className="text-lg font-semibold text-slate-950">Mint a new API token</h2>
            <p className="mt-2 text-sm leading-6 text-slate-600">
              Project tokens grant access only to the brands you select. Use clear names and the
              minimum scopes required for the workflow.
            </p>
            {brandName ? (
              <p className="mt-3 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700">
                Creating access for <span className="font-semibold">{brandName}</span>? Tokens are
                issued by you with an explicit brand grant.
              </p>
            ) : null}
          </div>
          <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-xs text-slate-600">
            <p className="font-semibold uppercase tracking-[0.16em] text-slate-500">One-time reveal</p>
            <p className="mt-2 max-w-[18rem]">
              The secret value is only shown once after creation. Copy it immediately into your
              secret manager.
            </p>
          </div>
        </div>

        <form className="mt-6 space-y-5" onSubmit={handleCreateToken}>
          <label className="block text-sm font-medium text-slate-700">
            Access boundary
            <select aria-label="Access boundary" value={authority} onChange={event => setAuthority(event.target.value as "project" | "account")} className="ml-3 rounded border p-2">
              <option value="project">Selected brands for one project</option>
              <option value="account">Account-wide access (all owned brands)</option>
            </select>
          </label>
          {authority === "project" ? <>
            <div className="grid gap-4 md:grid-cols-2">
              <Input ref={projectInputRef} label="Project ID" placeholder="thinkrun" maxLength={120} />
              <Input ref={environmentInputRef} label="Environment" placeholder="production" maxLength={120} />
              <Input ref={agentInputRef} label="Agent ID" placeholder="site-publisher" maxLength={120} />
              <Input ref={purposeInputRef} label="Purpose" placeholder="site-publishing" maxLength={120} />
            </div>
            <fieldset className="space-y-2">
              <legend className="text-sm font-medium text-slate-700">Allowed brands (required)</legend>
              {brandsQuery.isError ? <p>Unable to load your brands. Refresh before issuing project access.</p> : null}
              {(brandsQuery.data?.data ?? []).map(brand => <label key={brand.id} className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={selectedBrands.includes(brand.id)} onChange={event => setSelectedBrands(current => event.target.checked ? [...current, brand.id] : current.filter(id => id !== brand.id))} />
                {brand.name}
              </label>)}
            </fieldset>
          </> : <p className="text-sm text-slate-600">Account-wide tokens can access every brand you own, including future brands.</p>}

          <div className="grid gap-4 md:grid-cols-2">
            <Input
              ref={nameInputRef}
              label="Token name"
              placeholder="e.g. thinkrun-marketing"
              maxLength={120}
            />
            <Input
              ref={expiryInputRef}
              label={authority === "project" ? "Required expiry" : "Optional expiry"}
              type="datetime-local"
            />
          </div>

          <div className="space-y-3">
            <p className="text-sm font-medium text-slate-700">Scopes</p>
            <div className="grid gap-3 md:grid-cols-2">
              {CLI_AUTH_TOKEN_SCOPES.map((scope) => {
                const copy = SCOPE_COPY[scope];
                const checked = selectedScopes.includes(scope);
                return (
                  <label
                    key={scope}
                    className={`rounded-2xl border px-4 py-3 transition-colors ${
                      checked
                        ? "border-brand-300 bg-brand-50"
                        : "border-slate-200 bg-white hover:border-slate-300"
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <input
                        type="checkbox"
                        className="mt-1 h-4 w-4 rounded border-slate-300 text-brand-600 focus:ring-brand-500"
                        checked={checked}
                        onChange={() => toggleScope(scope)}
                      />
                      <div>
                        <p className="text-sm font-semibold text-slate-900">{copy.title}</p>
                        <p className="mt-1 text-xs text-slate-600">{copy.description}</p>
                        <p className="mt-2 font-mono text-[11px] text-slate-500">{scope}</p>
                      </div>
                    </div>
                  </label>
                );
              })}
            </div>
          </div>

          {formError ? (
            <p className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
              {formError}
            </p>
          ) : null}

          <div className="flex justify-end">
            <Button type="submit" loading={createToken.isPending}>
              Create token
            </Button>
          </div>
        </form>
      </Card>

      <Card className="rounded-[20px] border border-slate-200 bg-white shadow-sm">
        <div className="flex items-center justify-between gap-4">
          <div>
            <h2 className="text-lg font-semibold text-slate-950">Existing tokens</h2>
            <p className="mt-2 text-sm text-slate-600">
              Revoke tokens that no longer belong to an active consumer. Usage and revocation are
              tracked server-side.
            </p>
          </div>
          <Button
            type="button"
            variant="secondary"
            onClick={() => void tokensQuery.refetch()}
            disabled={tokensQuery.isFetching}
          >
            Refresh
          </Button>
        </div>

        {tokensQuery.isLoading ? (
          <div className="mt-6 flex min-h-[10rem] items-center justify-center">
            <div className="h-8 w-8 animate-spin rounded-full border-2 border-brand-600 border-t-transparent" />
          </div>
        ) : tokensQuery.isError ? (
          <div className="mt-6 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            Failed to load API tokens.
          </div>
        ) : sortedTokens.length === 0 ? (
          <div className="mt-6 rounded-2xl border border-dashed border-slate-300 bg-slate-50 px-5 py-8 text-center">
            <p className="text-sm font-medium text-slate-700">No API tokens yet.</p>
            <p className="mt-2 text-sm text-slate-500">
              Create one above for an external CLI consumer or automation.
            </p>
          </div>
        ) : (
          <div className="mt-6 space-y-4">
            {actionError ? (
              <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                {actionError}
              </div>
            ) : null}
            {sortedTokens.map((token) => {
              const revoked = Boolean(token.revokedAt);
              return (
                <div
                  key={token.id}
                  className="rounded-2xl border border-slate-200 bg-slate-50 px-5 py-4"
                >
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <p className="truncate text-sm font-semibold text-slate-950">{token.name}</p>
                        <span
                          className={`rounded-full px-2.5 py-1 text-[11px] font-semibold ${
                            revoked
                              ? "bg-slate-200 text-slate-700"
                              : "bg-emerald-100 text-emerald-700"
                          }`}
                        >
                          {revoked ? "Revoked" : "Active"}
                        </span>
                      </div>
                      <p className="mt-2 font-mono text-xs text-slate-500">{token.id}</p>
                      <p className="mt-2 text-xs text-slate-600">{token.projectGrant
                        ? `Project ${token.projectGrant.projectId} · ${token.projectGrant.environment} · ${token.projectGrant.agentId} · ${token.projectGrant.purpose} · Brands: ${token.projectGrant.brandIds.map(id => brandsQuery.data?.data.find(brand => brand.id === id)?.name ?? id).join(", ")}`
                        : "Account-wide: all owned brands"}</p>
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                      {!revoked ? (
                        <Button
                          type="button"
                          variant="danger"
                          size="sm"
                          loading={revokingTokenId === token.id}
                          onClick={() => void handleRevokeToken(token.id)}
                        >
                          Revoke
                        </Button>
                      ) : null}
                    </div>
                  </div>

                  <div className="mt-4 grid gap-3 text-xs text-slate-600 md:grid-cols-4">
                    <p>
                      <span className="font-semibold text-slate-700">Created</span>
                      <br />
                      {formatTimestamp(token.createdAt)}
                    </p>
                    <p>
                      <span className="font-semibold text-slate-700">Last used</span>
                      <br />
                      {formatTimestamp(token.lastUsedAt)}
                    </p>
                    <p>
                      <span className="font-semibold text-slate-700">Expires</span>
                      <br />
                      {formatTimestamp(token.expiresAt)}
                    </p>
                    <p>
                      <span className="font-semibold text-slate-700">Revoked</span>
                      <br />
                      {formatTimestamp(token.revokedAt)}
                    </p>
                  </div>

                  <div className="mt-4 flex flex-wrap gap-2">
                    {token.scopes.map((scope) => (
                      <span
                        key={`${token.id}-${scope}`}
                        className="rounded-full border border-slate-200 bg-white px-2.5 py-1 font-mono text-[11px] text-slate-600"
                      >
                        {scope}
                      </span>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Card>

      <Modal
        open={Boolean(revealedToken)}
        onClose={() => setRevealedToken(null)}
        title="Copy your new token now"
      >
        {revealedToken ? (
          <div className="space-y-4">
            <p className="text-sm text-slate-600">
              This secret will not be shown again. Move it into your vault or runtime secret store
              before closing this dialog.
            </p>
            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
              <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">
                Token for {revealedToken.metadata.name}
              </p>
              <pre className="mt-3 overflow-x-auto whitespace-pre-wrap break-all font-mono text-sm text-slate-900">
                {revealedToken.token}
              </pre>
            </div>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <CopyButton text={revealedToken.token} label="Copy token" />
              <Button type="button" variant="secondary" onClick={() => setRevealedToken(null)}>
                Done
              </Button>
            </div>
          </div>
        ) : null}
      </Modal>
    </div>
  );
}
